'use client';

/**
 * Header-Actions-Overflow ("Priority+"-Muster) — layout-unabhängig.
 *
 * Problem: Die Header-Topbar hat eine dynamisch wachsende Zahl von Icons
 * (Glocke, Warenkorb, Focus, Job-Status, Bug-Report, Assistent, Agent,
 * AI-Verbrauch, Container-Widgets ...). Unterhalb von `lg` (1024px) passt das
 * nicht mehr in eine Zeile: die Leiste lief über den Viewport hinaus und das
 * LETZTE Element — der Avatar mit Profil/Abmelden — war nicht mehr erreichbar
 * (kein Scroll, kein Wrap).
 *
 * Lösung: mobil bleiben nur die Top-Prioritäten sichtbar (Glocke · ⋯ · Avatar),
 * alles andere wandert in ein Bottom-Sheet mit beschrifteten Kacheln.
 *
 * Benutzung:
 *   {mobileMode ? (
 *     <HeaderActionsOverflow>
 *       <HeaderActionTile label="Warenkorb"><CartBadge /></HeaderActionTile>
 *       ...
 *     </HeaderActionsOverflow>
 *   ) : (
 *     <> ...alle Icons inline... </>
 *   )}
 *
 * Bewusst per JS-Bedingung statt `lg:hidden`/`hidden lg:flex`: so wird jede
 * Aktion nur EINMAL gemountet und pollende Widgets (AI-Verbrauch, Warenkorb,
 * Job-Status) laufen nicht doppelt.
 */

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { MoreHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';

/**
 * Eine Kachel im Overflow-Sheet: beliebiges Icon-Control + Beschriftung.
 * Rendert das Control `null` (z.B. CartBadge ohne Warenkorb, AiUsageWidget ohne
 * Quota-Daten), bleibt die Kachel via `has-[...]` automatisch unsichtbar —
 * keine leeren Platzhalter.
 */
export function HeaderActionTile({ label, className, children }) {
  return (
    <div
      className={cn(
        'hidden flex-col items-center gap-1.5 rounded-xl p-2 transition-colors',
        'hover:bg-muted/60 has-[a]:flex has-[button]:flex has-[img]:flex',
        className,
      )}
    >
      <div className="relative flex size-11 items-center justify-center">
        {children}
      </div>
      <span className="line-clamp-2 text-center text-[11px] leading-tight text-muted-foreground">
        {label}
      </span>
    </div>
  );
}

export function HeaderActionsOverflow({
  children,
  title = 'Weitere Aktionen',
  className,
}) {
  const [open, setOpen] = useState(false);
  const pathname = usePathname();

  // Sheet schließen, sobald eine Aktion navigiert.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          mode="icon"
          shape="circle"
          className={cn(
            'size-9 shrink-0 hover:bg-primary/10 hover:[&_svg]:text-primary',
            className,
          )}
          title={title}
          aria-label={title}
          data-testid="header-actions-overflow"
        >
          <MoreHorizontal className="size-4.5!" />
        </Button>
      </SheetTrigger>

      <SheetContent
        side="bottom"
        className="max-h-[70vh] gap-0 rounded-t-2xl p-0"
      >
        <SheetHeader className="border-b px-5 py-4">
          <SheetTitle className="text-start text-base">{title}</SheetTitle>
        </SheetHeader>

        <SheetBody className="overflow-y-auto p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">{children}</div>
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}

export default HeaderActionsOverflow;
